/**
 * Service d'analyse des données pour suggérer des configurations de graphiques
 */

export interface FieldInfo {
  name: string;
  type: 'numeric' | 'categorical' | 'date' | 'text' | 'unknown';
  displayName: string;
  canBeAxis: boolean;
  canBeMetric: boolean;
  canBeSeries: boolean;
  canBeFilter: boolean;
  sampleValues?: any[];
  uniqueCount?: number;
  // Relations code/nom
  relatedCodeField?: string; // Si c'est un nom, son code associé
  relatedNameField?: string; // Si c'est un code, son nom associé
  isCode?: boolean; // Indique si c'est un champ code
  isName?: boolean; // Indique si c'est un champ nom
}

export interface ChartSuggestion {
  type: 'bar' | 'line' | 'pie' | 'area' | 'scatter';
  xAxis: string;
  yAxis?: string;
  series?: string[];
  aggregation?: 'sum' | 'avg' | 'count' | 'min' | 'max';
  confidence: number; // 0-1
  description: string;
}

/**
 * Analyse les résultats de recherche pour identifier les champs disponibles
 * Amélioré pour mieux détecter les types numériques même s'ils sont stockés comme strings
 */
export function analyzeSearchResults(results: any[]): FieldInfo[] {
  if (!results || results.length === 0) return [];

  const fieldMap = new Map<string, FieldInfo>();

  // Analyser chaque résultat - passer plusieurs fois pour améliorer la détection
  // Premier passage : collecter toutes les valeurs
  results.forEach((result) => {
    Object.keys(result).forEach((key) => {
      // Ignorer les champs système
      if (key.startsWith('_')) return;

      const value = result[key];
      if (value === null || value === undefined) return;

      if (!fieldMap.has(key)) {
        fieldMap.set(key, {
          name: key,
          type: 'unknown',
          displayName: formatFieldName(key),
          canBeAxis: false,
          canBeMetric: false,
          canBeSeries: false,
          canBeFilter: true,
          sampleValues: [],
          uniqueCount: 0,
        });
      }

      const fieldInfo = fieldMap.get(key)!;
      
      // Collecter des exemples de valeurs
      if (fieldInfo.sampleValues!.length < 10) {
        const valueStr = String(value);
        if (!fieldInfo.sampleValues!.some(v => String(v) === valueStr)) {
          fieldInfo.sampleValues!.push(value);
        }
      }
    });
  });

  // Deuxième passage : analyser les types avec toutes les valeurs collectées
  fieldMap.forEach((fieldInfo, key) => {
    // Analyser toutes les valeurs pour déterminer le type le plus probable
    const allValues = results
      .map((r) => r[key])
      .filter((v) => v !== null && v !== undefined);

    if (allValues.length === 0) return;

    // Compter les types détectés
    const typeCounts: { [key: string]: number } = {
      numeric: 0,
      categorical: 0,
      date: 0,
      text: 0,
      unknown: 0,
    };

    allValues.forEach((value) => {
      const detectedType = detectFieldType(value, fieldInfo.type);
      typeCounts[detectedType] = (typeCounts[detectedType] || 0) + 1;
    });

    // Déterminer le type majoritaire
    const maxType = Object.keys(typeCounts).reduce((a, b) =>
      typeCounts[a] > typeCounts[b] ? a : b
    );

    // Si au moins 50% des valeurs sont numériques, considérer comme numérique
    // (seuil réduit pour mieux détecter les champs comme "Price")
    const numericRatio = typeCounts.numeric / allValues.length;
    if (numericRatio >= 0.5) {
      fieldInfo.type = 'numeric';
    } else {
      fieldInfo.type = maxType as FieldInfo['type'];
    }
    
    // Détection spéciale pour les champs avec "price", "cost", "amount", "value", etc.
    const lowerKey = key.toLowerCase();
    if ((lowerKey.includes('price') || 
         lowerKey.includes('cost') || 
         lowerKey.includes('amount') || 
         lowerKey.includes('value') ||
         lowerKey.includes('quantity') ||
         lowerKey.includes('qty') ||
         lowerKey.includes('count')) && 
        numericRatio >= 0.3) {
      // Si au moins 30% sont numériques et le nom suggère un nombre, forcer numérique
      fieldInfo.type = 'numeric';
    }
  });

  // Détecter les relations code/nom
  detectCodeNameRelations(fieldMap);

  // Analyser les champs pour déterminer leurs capacités
  const analyzedFields = Array.from(fieldMap.values()).map((field) => {
    return analyzeFieldCapabilities(field, results);
  });

  return analyzedFields;
}

/**
 * Détecte les relations entre champs code et nom
 * Ex: domainCode → domain, areaCode → area, elementCode → element, etc.
 */
function detectCodeNameRelations(fieldMap: Map<string, FieldInfo>) {
  const codePatterns = [
    { code: /^(.+)Code$/i, name: /^(.+)$/ },
    { code: /^(.+)\s+Code$/i, name: /^(.+)$/ },
    { code: /^(.+)Code\s+\(.+\)$/i, name: /^(.+)$/ },
  ];

  fieldMap.forEach((field, fieldName) => {
    // Détecter si c'est un champ code
    for (const pattern of codePatterns) {
      const codeMatch = fieldName.match(pattern.code);
      if (codeMatch) {
        const baseName = codeMatch[1].trim();
        
        // Chercher le champ nom correspondant
        fieldMap.forEach((otherField, otherFieldName) => {
          if (otherFieldName.toLowerCase() === baseName.toLowerCase()) {
            // Relation trouvée !
            field.isCode = true;
            field.relatedNameField = otherFieldName;
            field.canBeFilter = true; // Les codes peuvent être des filtres
            field.canBeAxis = false; // Préférer utiliser le nom pour l'axe
            
            otherField.isName = true;
            otherField.relatedCodeField = fieldName;
            otherField.canBeAxis = true; // Les noms sont meilleurs pour les axes
            otherField.canBeFilter = true;
          }
        });
      }
    }
  });

  // Détecter aussi les patterns inverses (nom → code)
  fieldMap.forEach((field, fieldName) => {
    if (field.isName) return; // Déjà traité
    
    // Chercher un champ code correspondant
    fieldMap.forEach((otherField, otherFieldName) => {
      if (otherField.isCode) return; // Déjà traité
      
      // Patterns communs: domainCode → domain, areaCode → area
      const codeVariations = [
        `${fieldName}Code`,
        `${fieldName} Code`,
        `${fieldName}Code (M49)`,
        `${fieldName} Code (M49)`,
        `${fieldName}Code (CPC)`,
        `${fieldName} Code (CPC)`,
      ];
      
      if (codeVariations.some(code => code.toLowerCase() === otherFieldName.toLowerCase())) {
        field.isName = true;
        field.relatedCodeField = otherFieldName;
        field.canBeAxis = true;
        
        otherField.isCode = true;
        otherField.relatedNameField = fieldName;
        otherField.canBeFilter = true;
      }
    });
  });
}

/**
 * Détecte le type d'un champ basé sur sa valeur
 */
function detectFieldType(value: any, currentType: string): FieldInfo['type'] {
  // Si déjà déterminé comme date, garder
  if (currentType === 'date') return 'date';
  // Si déjà déterminé comme numérique, garder
  if (currentType === 'numeric') return 'numeric';

  // Vérifier si c'est une date
  if (typeof value === 'string') {
    const datePattern = /^\d{4}-\d{2}-\d{2}/;
    if (datePattern.test(value) || !isNaN(Date.parse(value))) {
      return 'date';
    }
    
    // Vérifier si c'est un nombre stocké comme string
    // Détecter les nombres (entiers et décimaux)
    const numericPattern = /^-?\d+(\.\d+)?$/;
    if (numericPattern.test(value.trim())) {
      return 'numeric';
    }
    
    // Détecter les plages numériques (ex: "25000-35000", "100-200", "1.5-2.5")
    const rangePattern = /^\d+(\.\d+)?\s*[-–—]\s*\d+(\.\d+)?$/;
    if (rangePattern.test(value.trim())) {
      return 'numeric';
    }
    
    // Détecter les nombres avec séparateurs (ex: "25,000", "1,234.56")
    const numberWithSeparators = /^-?\d{1,3}(,\d{3})*(\.\d+)?$/;
    if (numberWithSeparators.test(value.trim())) {
      return 'numeric';
    }
    
    // Détecter les nombres avec unités monétaires (ex: "$25000", "25000 USD", "€1000")
    const currencyPattern = /^[$€£¥]?\s*\d+(\.\d+)?\s*(USD|EUR|GBP|JPY|CNY)?$/i;
    if (currencyPattern.test(value.trim())) {
      return 'numeric';
    }
  }

  // Vérifier si c'est numérique
  if (typeof value === 'number') {
    return 'numeric';
  }

  // Vérifier si c'est un booléen (traiter comme catégoriel)
  if (typeof value === 'boolean') {
    return 'categorical';
  }

  // Si c'est une string, déterminer si c'est catégoriel ou text
  if (typeof value === 'string') {
    // Court → catégorie (même avec espaces : "Hen eggs in shell, fresh",
    // "United States" sont des catégories, pas du texte libre)
    if (value.length < 80) {
      return 'categorical';
    }
    return 'text';
  }

  return 'unknown';
}

/**
 * Analyse les capacités d'un champ
 */
function analyzeFieldCapabilities(field: FieldInfo, results: any[]): FieldInfo {
  const values = results
    .map((r) => r[field.name])
    .filter((v) => v !== null && v !== undefined);

  const uniqueValues = new Set(values);
  field.uniqueCount = uniqueValues.size;

  switch (field.type) {
    case 'numeric':
      field.canBeMetric = true;
      field.canBeAxis = uniqueValues.size < 50; // Peut être axe si peu de valeurs uniques
      field.canBeSeries = false;
      break;

    case 'categorical':
      field.canBeAxis = true;
      field.canBeSeries = true;
      field.canBeMetric = false;
      break;

    case 'date':
      field.canBeAxis = true;
      field.canBeSeries = false;
      field.canBeMetric = false;
      break;

    case 'text':
      // Texte libre (descriptions, notes) — l'agrégation ES limite déjà les
      // buckets (top 30 axe / top 12 série), donc un seuil généreux suffit
      field.canBeAxis = uniqueValues.size < 300;
      field.canBeSeries = uniqueValues.size < 50;
      field.canBeMetric = false;
      break;

    default:
      field.canBeAxis = false;
      field.canBeMetric = false;
      field.canBeSeries = false;
  }

  return field;
}

/**
 * Génère des suggestions de graphiques basées sur les champs disponibles
 */
export function generateChartSuggestions(
  fields: FieldInfo[],
  indexName: string
): ChartSuggestion[] {
  const suggestions: ChartSuggestion[] = [];

  const numericFields = fields.filter((f) => f.canBeMetric);
  const categoricalFields = fields.filter((f) => f.canBeAxis && f.type !== 'date');
  const dateFields = fields.filter((f) => f.type === 'date');
  const seriesFields = fields.filter((f) => f.canBeSeries);

  // Suggestion 1: Graphique en barres simple (catégorie vs métrique)
  if (categoricalFields.length > 0 && numericFields.length > 0) {
    const catField = categoricalFields[0];
    const numField = numericFields[0];
    
    suggestions.push({
      type: 'bar',
      xAxis: catField.name,
      yAxis: numField.name,
      aggregation: 'sum',
      confidence: 0.9,
      description: `Comparer ${numField.displayName} par ${catField.displayName}`,
    });
  }

  // Suggestion 2: Série temporelle (date vs métrique)
  if (dateFields.length > 0 && numericFields.length > 0) {
    const dateField = dateFields[0];
    const numField = numericFields[0];
    
    suggestions.push({
      type: 'line',
      xAxis: dateField.name,
      yAxis: numField.name,
      aggregation: 'avg',
      confidence: 0.85,
      description: `Évolution de ${numField.displayName} dans le temps`,
    });
  }

  // Suggestion 3: Graphique en secteurs (distribution)
  if (categoricalFields.length > 0 && numericFields.length > 0) {
    const catField = categoricalFields[0];
    const numField = numericFields[0];
    
    if (catField.uniqueCount && catField.uniqueCount < 10) {
      suggestions.push({
        type: 'pie',
        xAxis: catField.name,
        yAxis: numField.name,
        aggregation: 'sum',
        confidence: 0.8,
        description: `Distribution de ${numField.displayName} par ${catField.displayName}`,
      });
    }
  }

  // Suggestion 4: Graphique avec séries multiples
  if (categoricalFields.length > 0 && numericFields.length > 0 && seriesFields.length > 0) {
    const catField = categoricalFields[0];
    const numField = numericFields[0];
    const seriesField = seriesFields[0];
    
    suggestions.push({
      type: 'bar',
      xAxis: catField.name,
      yAxis: numField.name,
      series: [seriesField.name],
      aggregation: 'sum',
      confidence: 0.75,
      description: `Comparer ${numField.displayName} par ${catField.displayName} et ${seriesField.displayName}`,
    });
  }

  // Suggestion 5: Graphique en aires (série temporelle)
  if (dateFields.length > 0 && numericFields.length > 0) {
    const dateField = dateFields[0];
    const numField = numericFields[0];
    
    suggestions.push({
      type: 'area',
      xAxis: dateField.name,
      yAxis: numField.name,
      aggregation: 'avg',
      confidence: 0.8,
      description: `Tendance de ${numField.displayName} dans le temps`,
    });
  }

  // Trier par confiance
  return suggestions.sort((a, b) => b.confidence - a.confidence);
}

/**
 * Détecte les patterns courants dans les noms de champs
 */
export function detectCommonPatterns(fields: FieldInfo[], indexName: string): {
  areaField?: string;
  valueField?: string;
  yearField?: string;
  itemField?: string;
  elementField?: string;
} {
  const patterns: any = {};

  fields.forEach((field) => {
    const lowerName = field.name.toLowerCase();
    
    if (lowerName.includes('area') || lowerName.includes('country') || lowerName.includes('region')) {
      patterns.areaField = field.name;
    }
    
    if (lowerName.includes('value') || lowerName.includes('price') || lowerName.includes('amount')) {
      patterns.valueField = field.name;
    }
    
    if (lowerName.includes('year') || lowerName.includes('date') || lowerName.includes('time')) {
      patterns.yearField = field.name;
    }
    
    if (lowerName.includes('item') || lowerName.includes('product') || lowerName.includes('commodity')) {
      patterns.itemField = field.name;
    }
    
    if (lowerName.includes('element') || lowerName.includes('measure') || lowerName.includes('metric')) {
      patterns.elementField = field.name;
    }
  });

  return patterns;
}

/**
 * Formate un nom de champ pour l'affichage
 */
function formatFieldName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Valide une configuration de graphique
 */
export function validateChartConfig(config: {
  type: string;
  xAxis?: string;
  yAxis?: string;
  fields: FieldInfo[];
}): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!config.type) {
    errors.push('Le type de graphique est requis');
  }

  if (!config.xAxis) {
    errors.push('L\'axe X est requis');
  } else {
    const xField = config.fields.find((f) => f.name === config.xAxis);
    if (!xField) {
      errors.push(`Le champ ${config.xAxis} n'existe pas`);
    } else if (!xField.canBeAxis) {
      errors.push(`Le champ ${config.xAxis} ne peut pas être utilisé comme axe`);
    }
  }

  if (config.type !== 'pie' && !config.yAxis) {
    errors.push('L\'axe Y est requis pour ce type de graphique');
  } else if (config.yAxis) {
    const yField = config.fields.find((f) => f.name === config.yAxis);
    if (!yField) {
      errors.push(`Le champ ${config.yAxis} n'existe pas`);
    } else if (!yField.canBeMetric) {
      errors.push(`Le champ ${config.yAxis} ne peut pas être utilisé comme métrique`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
